import { useState, useEffect, useCallback, useRef } from 'react';
import { getAdsFiltered, type AdFilter, type AdSort, type AdFocus } from '../db/database';
import { DEFAULT_PAGE_SIZE } from '@barter/shared';
import type { Ad, AdCondition } from '@barter/shared';

interface UseAdsState {
  items: Ad[];
  total: number;
  hasMore: boolean;
  loading: boolean;
  error: string | null;
}

interface AdListParamsLike {
  category?: string;
  city?: string;
  search?: string;
  sellerId?: number;
  status?: Ad['status'];
  condition?: AdCondition;
  priceMin?: number;
  priceMax?: number;
  make?: string;
  model?: string;
  days?: number;
  focus?: AdFocus;
  sort?: AdSort;
}

export type AdListParams = AdListParamsLike;

function paramsToFilter(params?: AdListParamsLike): AdFilter {
  return {
    category: params?.category,
    city: params?.city,
    search: params?.search,
    sellerId: params?.sellerId,
    status: params?.status,
    condition: params?.condition,
    priceMin: params?.priceMin,
    priceMax: params?.priceMax,
    make: params?.make,
    model: params?.model,
    days: params?.days,
    focus: params?.focus,
    sort: params?.sort,
  };
}

/**
 * Все чтения идут из локальной Dexie-базы (снапшот из Cloudflare подтягивается
 * отдельно, см. services/snapshot.ts, и сигналит событием 'barter:new-ads').
 */
export function useAds(params?: AdListParamsLike) {
  const [refreshKey, setRefreshKey] = useState(0);
  const [page, setPage] = useState(1);
  const [state, setState] = useState<UseAdsState>({
    items: [],
    total: 0,
    hasMore: false,
    loading: true,
    error: null,
  });

  const paramsKey = JSON.stringify(params);
  // Эпоха списка: растёт при смене параметров/refresh — опоздавший loadMore
  // со старыми параметрами не дописывает свои результаты в новый список.
  const epochRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const epoch = ++epochRef.current;
    setPage(1);
    setState((prev) => ({ ...prev, loading: true, error: null }));

    (async () => {
      try {
        const local = await getAdsFiltered(paramsToFilter(params), 1, DEFAULT_PAGE_SIZE);
        if (!cancelled && epoch === epochRef.current) {
          setState({
            items: local.items,
            total: local.total,
            hasMore: local.hasMore,
            loading: false,
            error: null,
          });
        }
      } catch (err) {
        if (!cancelled && epoch === epochRef.current) {
          setState((prev) => ({ ...prev, loading: false, error: (err as Error).message }));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey, refreshKey]);

  useEffect(() => {
    const handleNewAds = () => setRefreshKey((k) => k + 1);
    window.addEventListener('barter:new-ads', handleNewAds);
    return () => window.removeEventListener('barter:new-ads', handleNewAds);
  }, []);

  const loadMore = useCallback(async () => {
    if (state.loading || !state.hasMore) return;

    const epoch = epochRef.current;
    const nextPage = page + 1;
    setState((prev) => ({ ...prev, loading: true }));

    try {
      const local = await getAdsFiltered(paramsToFilter(params), nextPage, DEFAULT_PAGE_SIZE);
      if (epoch !== epochRef.current) return; // параметры сменились — результат не актуален
      setState((prev) => ({
        ...prev,
        items: [...prev.items, ...local.items],
        total: local.total,
        hasMore: local.hasMore,
        loading: false,
      }));
      setPage(nextPage);
    } catch (err) {
      if (epoch === epochRef.current) {
        setState((prev) => ({ ...prev, loading: false, error: (err as Error).message }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, state.loading, state.hasMore, paramsKey]);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  return { ...state, loadMore, refresh };
}
